import React from 'react';

import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';

import { useDispatch } from 'store/configureStore';

import { googleSignIn, trackUserLoginSignup } from 'actions';
import {
  GOOGLE_CLIENT_ID_DEV,
  GOOGLE_CLIENT_ID_PROD,
  PROD_DOMAINS_FOR_SOCIAL_SIGN_IN,
} from 'constants/index';
import {
  UserLoginSource,
  UserLoginType,
} from 'contexts/LoginContext/LoginContext';
import { useLoginOrigin } from 'hooks/useLoginOrigin/useLoginOrigin';
import { useTranslations } from 'utils';
import { url } from 'utils/url/url';

import { ReactComponent as GoogleIcon } from './assets/googleIcon.svg';

const getAppId = () => {
  const domainName = url.getDomainName();
  return PROD_DOMAINS_FOR_SOCIAL_SIGN_IN.includes(domainName)
    ? GOOGLE_CLIENT_ID_PROD
    : GOOGLE_CLIENT_ID_DEV;
};

export function GoogleSignIn({
  isLogin,
  onClick,
  onSuccess,
  onError,
}: {
  isLogin: boolean;
  onClick: (handleProviderSignIn: () => void) => void;
  onSuccess?: () => void;
  onError: (err: unknown) => void;
}) {
  const clientId = getAppId();

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <GoogleSignInButton
        isLogin={isLogin}
        onClick={onClick}
        onError={onError}
        onSuccess={onSuccess}
      />
    </GoogleOAuthProvider>
  );
}

function GoogleSignInButton({
  isLogin,
  onClick,
  onSuccess,
  onError,
}: {
  isLogin: boolean;
  onClick: (handleProviderSignIn: () => void) => void;
  onSuccess?: () => void;
  onError: (err: unknown) => void;
}) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const { loginOrigin } = useLoginOrigin();

  const handleResponse = async (authCode: string) => {
    try {
      await dispatch(
        googleSignIn({
          authCode,
        }),
      );
      onSuccess?.();
      dispatch(
        trackUserLoginSignup(
          isLogin ? UserLoginType.LOGIN : UserLoginType.SIGNUP,
          UserLoginSource.GOOGLE,
          loginOrigin,
        ),
      );
    } catch (error) {
      onError?.(error);
    }
  };

  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: codeResponse => {
      handleResponse(codeResponse.code);
    },
    onError: error => {
      onError(error);
    },
    flow: 'auth-code',
  });

  return (
    <>
      <button
        className={`
        mb-4 flex w-full cursor-pointer flex-row items-center justify-center gap-2
        rounded-md border border-gray-300
        bg-white px-6 py-2.5 text-blue-600 shadow-[0_1px_1px_rgba(0,0,0,0.08)]
        disabled:opacity-50
      `}
        onClick={() => onClick(handleGoogleSignIn)}
        data-testid="googleSignInButton"
        type="submit"
      >
        <GoogleIcon className="w-6" />
        <span className="text-base text-gray-950">
          {i18n.t(
            isLogin
              ? 'userLogin.social.google.login'
              : 'userLogin.social.google.signup',
          )}
        </span>
      </button>
    </>
  );
}
